import {
    Component,
    OnInit,
    OnDestroy,
    OnChanges,
    SimpleChanges,
    Input,
    EventEmitter,
    Output,
    ElementRef,
    inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { PSIC_COMPONENTS } from '@pacificspecialtyinsurancecompany/psic-ng-components';
import { Store } from '@ngrx/store';
import { PolicyState } from '../store/policy/policy.reducer';
import { combineLatest, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { globalMessages } from '../shared/global-messages';
import { AnimationComponent } from '../shared/animation/animation.component';

@Component({
    selector: 'app-latest-activities',
    imports: [CommonModule, MatIcon, ...PSIC_COMPONENTS, AnimationComponent],
    templateUrl: './latest-activities.component.html',
    styleUrl: './latest-activities.component.scss',
})
export class LatestActivitiesComponent implements OnInit, OnDestroy, OnChanges {
    @Input() isLoaded = false;
    @Input() dcPolicyFailed = false;
    @Input() dcBillingFailed = false;
    @Output() retry = new EventEmitter<void>();
    globalMessages = globalMessages;
    latestActFailed: boolean = false;
    activities: any[] = [];
    displayedActivities: any[] = [];
    policyNumber: string = '';
    private el = inject(ElementRef);
    private combinedSubscription: Subscription | undefined;

    constructor(private store: Store<{ policy: PolicyState }>) {}

    ngOnChanges(changes: SimpleChanges) {
        if (changes['dcPolicyFailed'] || changes['dcBillingFailed']) {
            this.latestActFailed = this.dcPolicyFailed || this.dcBillingFailed;
        }
    }

    ngOnInit() {
        this.latestActFailed = this.dcPolicyFailed || this.dcBillingFailed;
        this.combinedSubscription = combineLatest([
            this.store.select((state) => state.policy.data).pipe(filter((data) => !!data)),
            this.store.select((state) => state.policy.policyNumber).pipe(filter((policyNumber) => !!policyNumber)),
        ]).subscribe(([policyData, policyNumber]) => {
            this.loadActivities(policyData, policyNumber!);
        });
    }
    private buildPolicyDescription(activity: any): string {
        const type = (activity.activityType?.value || '').toLowerCase();
        const effDate = activity.effectiveDate?.value || '';
        const reason = (activity.reasons || [])
            .map((r: any) => r.reasonDescription?.value || '')
            .filter((v: string) => !!v)
            .join('. ');

        if (type.includes('renew') && !type.includes('non')) return `Policy Renewed effective ${effDate}`;
        if (type.includes('non-renew') || type.includes('nonrenew'))
            return `Policy Non-Renewed effective ${effDate}${reason ? ' due to ' + reason : ''}`;
        if (type.includes('rescind'))
            return `Policy Cancel-Pending${reason ? ' due to ' + reason : ''} has been Rescinded`;
        if (type.includes('cancel-pending') || type.includes('cancel pending'))
            return `Policy is in Cancel-pending effective ${effDate}${reason ? ' due to ' + reason : ''}`;
        if (type.includes('cancel')) return `Policy Cancelled effective ${effDate}${reason ? ' due to ' + reason : ''}`;
        if (type.includes('endorse')) return `Policy endorsed effective ${effDate}${reason ? ' due to ' + reason : ''}`;
        return reason;
    }

    private loadActivities(policyData: any, policyKey: string) {
        const policySnapshot = policyData[policyKey]?.policySnapshot;
        const billingSnapshot = policyData[policyKey]?.billingSnapshot;

        if (!policySnapshot && !billingSnapshot) {
            return;
        }

        this.policyNumber = policyKey || '';
        const latestActivities = policySnapshot?.latestActivities || {};

        // Policy activities may come from policySnapshot.latestActivities OR from
        // the billing snapshot root — use whichever has data, avoiding duplicates.
        const policyActivitiesFromPolicy = latestActivities.policyActivities || [];
        const policyActivitiesFromBilling = billingSnapshot?.policyActivities || [];
        // Prefer billing snapshot's copy if it exists (it's the definitive combined response),
        // otherwise fall back to the policy snapshot copy.
        const policyActivities = [ ...policyActivitiesFromBilling , ...policyActivitiesFromPolicy]
            const billingActivities = billingSnapshot?.billingActivities || [];

        // Don't overwrite with an empty/partial result — wait for a more complete emission.
        if (!policyActivities.length && !billingActivities.length) {
            return;
        }
        // Transform policy activities
        const transformedPolicyActivities = policyActivities.map((activity: any) => {
            const rawDate = activity.activityDate?.value || '';
            return {
                category: activity.activityType?.value || '',
                rawDate: rawDate,
                date: this.formatDate(rawDate),
                description: this.buildPolicyDescription(activity),
                badges: ['Transactions Info'],
                action: null,
                amount: null,
                linkType: 'policy',
                linkId: this.policyNumber,
            };
        });

        // Transform billing activities
        const transformedBillingActivities = billingActivities.map((activity: any) => {
            let description = activity?.activityDescription?.value || '';

            // Replace ^1, ^2, etc. with corresponding link texts based on id
            const links = activity.links?.value || [];
            links.forEach((link: any) => {
                const placeholder = `^${link.id}`;
                const linkText = link.linkText || '';
                description = description.replace(new RegExp(`\\${placeholder}`, 'g'), linkText);
            });
            
            const rawDate = activity?.activityTransactionDateTime?.value || '';
            const linkType = activity?.activityLinkType?.value || '';
            const linkId = activity?.activityLinkIdValue?.value || activity?.activityLinkId?.value || '';

            return {
                category: activity.activityType?.value || '',
                rawDate: rawDate,
                date: this.formatDate(rawDate),
                description: description,
                badges: ['In/out Communications'],
                action: null,
                amount: activity?.activityAmount?.value,
                linkType: linkType,
                linkId: linkId,
            };
        });

        // Combine and sort by raw ISO date string (newest first); exclude any with unparseable dates
        const allSorted = [...transformedPolicyActivities, ...transformedBillingActivities]
            .filter((a) => a.rawDate && !isNaN(new Date(a.rawDate).getTime()))
            .sort((a, b) => new Date(b.rawDate).getTime() - new Date(a.rawDate).getTime());

        // Filtering rules:
        // 1. Show all activities from the last 15 days (today-15 to end of today).
        //    Future dates are excluded from this window.
        // 2. If none fall in that window, fall back to the 5 most recent from the
        //    full array (future dates and pre-15-day entries included as fallback).
        const now = new Date();
        now.setHours(23, 59, 59, 999);
        const fifteenDaysAgo = new Date(now);
        fifteenDaysAgo.setDate(fifteenDaysAgo.getDate() - 15);
        fifteenDaysAgo.setHours(0, 0, 0, 0);

        const withinLast15Days = allSorted.filter((a) => {
            const t = new Date(a.rawDate).getTime();
            return t >= fifteenDaysAgo.getTime() && t <= now.getTime();
        });

        this.activities = withinLast15Days.length > 0 ? withinLast15Days : allSorted.slice(0, 5);

        // Initially show the first 5; user can expand to see all via "Show more".
        this.displayedActivities = this.activities.slice(0, 5);
    }

    private formatDate(dateString: string): string {
        if (!dateString) return '';
        try {
            const date = new Date(dateString);
            const now = new Date();

            // Calculate the difference in milliseconds
            const diffTime = now.getTime() - date.getTime();
            const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

            // If within 1 day, show relative format
            if (diffDays === 0) {
                const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
                if (diffHours === 0) {
                    const diffMinutes = Math.floor(diffTime / (1000 * 60));
                    if (diffMinutes === 0) {
                        return 'Just now';
                    }
                    return `${diffMinutes} minute${diffMinutes !== 1 ? 's' : ''} ago`;
                }
                return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
            } else if (diffDays === 1) {
                return '1 day ago';
            }

            // Otherwise, show MM-DD-YYYY format
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const year = date.getFullYear();

            return `${month}-${day}-${year}`;
        } catch {
            return dateString;
        }
    }

    // Returns the default collapsed view: first 5 of the already-filtered activities.
    protected getDefaultActivities(): any[] {
        return this.activities.slice(0, 5);
    }

    loadMoreActivities() {
        this.displayedActivities = this.activities;
    }

    showLessActivities() {
        this.displayedActivities = this.activities.slice(0, 5);
        this.el.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    ngOnDestroy() {
        this.combinedSubscription?.unsubscribe();
    }

    retryLatestActivities() {
        this.retry.emit();
    }
}
